import { Spinner } from "@blueprintjs/core";
import { ButtonContainer, IDataTableColumn, useApiEffect, useGridState, useSearchApi } from "nsitools-react";
import * as React from "react";
import { useHistory } from "react-router-dom";

import { CompetitionRequestApi, CompetitionRequestGridDto, CompetitionRequestSearch, PersonApi } from "../../../../api";
import { AddButton, ViewButton } from "../../../../components";
import { useAuth, useGlobalData } from "../../../../contexts";
import { useApiService, useTl } from "../../../../hooks";
import { ETLCodes } from "../../../../locales";
import { DrupalDataTable } from "../../../common";
import { DrupalFieldSet } from "../../../common/DurpalFieldSet";
import { EDrupalRoutes } from "../../../EDrupalRoutes";

export interface IDrupalCompetitionRequestListProps {}

export const DrupalCompetitionRequestList: React.FunctionComponent<IDrupalCompetitionRequestListProps> = (props) => {
  const api = useApiService(CompetitionRequestApi);
  const personApi = useApiService(PersonApi);
  const { user } = useAuth();
  const { t } = useTl();
  const history = useHistory();
  const { currentLanguage } = useGlobalData();
  const [clubIds, clubIdsLoading] = useApiEffect(() => personApi.getLinkedClubs(), []);

  const tableState = useGridState<any>({
    serverMode: true,
    enablePagination: true,
    enableFilter: true,
    sortKeys: { requestDate: "DESC" },
    availablePageSizes: [5, 10, 15],
    pageSize: 5,
  });
  const { totalCount } = tableState;
  const searchFunction = React.useCallback(
    (sObj?) => {
      const sobj = CompetitionRequestSearch.fromJS({
        ...sObj,
        languageId: currentLanguage.languageId,
        personId: user.personId,
      });
      return api.baseSearch(sobj);
    },
    [api, currentLanguage.languageId, user.personId]
  );

  const { loading } = useSearchApi({
    searchFunction,
    tableState,
    initialSearch: true,
    trimSearchObjectStringValues: true,
  });

  const columns = React.useMemo<IDataTableColumn[]>(
    () => [
      {
        computed: true,
        disableSorting: true,
        fieldName: "actions",
        autoFitContent: true,
        render: (row: CompetitionRequestGridDto) => (
          <ButtonContainer>
            <ViewButton
              minimal={true}
              onClick={() => history.push(`${EDrupalRoutes.competitionRequest}/${row.competitionRequestId}`)}
              secured={false}
            />
          </ButtonContainer>
        ),
      },
      {
        header: () => t(ETLCodes.Club),
        fieldName: "clubName",
      },
      {
        header: () => t(ETLCodes.Competition),
        fieldName: "competitionName",
        computed: true,
        disableSorting: true,
      },
      {
        header: () => t(ETLCodes.RequestNumber),
        fieldName: "requestNumber",
      },
      {
        header: () => t(ETLCodes.RequestDate),
        fieldName: "requestDate",
      },
      {
        header: () => t(ETLCodes.DateFrom),
        fieldName: "dateFrom",
      },
      {
        header: () => t(ETLCodes.DateTo),
        fieldName: "dateTo",
      },
      {
        header: () => t(ETLCodes.RequestState),
        fieldName: "requestState",
      },
    ],
    [history, t]
  );

  return !!clubIds && clubIds.length > 0 && !clubIdsLoading ? (
    <DrupalFieldSet
      title={`${t(ETLCodes.Competitions)} (${totalCount})`}
      rightElement={
        <AddButton
          secured={false}
          text={t(ETLCodes.DrupalCreateNewRequest)}
          onClick={() => history.push(`${EDrupalRoutes.portalNewCompetitionRequest}`)}
        />
      }
    >
      <DrupalDataTable tableState={tableState} loading={loading} columns={columns} />
    </DrupalFieldSet>
  ) : null;
};
